<template>
  <div>
    <!-- Two parts: Site (project daily logs) and Office (kiosk time logs). -->
    <b-tabs pills small nav-class="mb-3">
      <b-tab active>
        <template v-slot:title>
          <font-awesome-icon icon="road" /> Site Attendance
          <b-badge variant="light" class="ml-1">{{ siteCount }}</b-badge>
        </template>

        <b-row class="m-0">
          <b-col class="p-0">
            <fieldset class="border p-3">
              <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6">SITE ATTENDANCE SHEET</legend>
              <table class="table table-bordered mb-0 table-sm">
                <thead class="thead-dark">
                  <tr>
                    <th>Date</th>
                    <th>Position</th>
                    <th>Rate</th>
                    <th>Regular Hours</th>
                    <th>OT Hours</th>
                    <th>Total Rate</th>
                    <th>Project/ Item</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row_log, index) in params.logs" :key="'site-' + index">
                    <td>
                      <strong class="cursor-pointer btn-link font-weight-bold" @click="viewDetails(row_log)">{{ row_log.transaction_date }}</strong>
                    </td>
                    <td>{{ row_log.description }}</td>
                    <td><number-format-component :input_value="Number(row_log.daily_rate)" /></td>
                    <td>{{ row_log.regular_hours }}</td>
                    <td>{{ row_log.ot_hours }}</td>
                    <td><number-format-component :input_value="Number(row_log.total)" /></td>
                    <th>
                      {{ row_log.project_description }} | {{ row_log.location_description }} | {{ row_log.structure_description }} | {{ row_log.item_description }}
                    </th>
                    <td>
                      <b-badge v-if="row_log.payroll_id != null" variant="success">PAID</b-badge>
                      <b-badge v-else variant="warning">UNPROCESSED</b-badge>
                    </td>
                  </tr>
                  <tr v-if="!params.logs || params.logs.length === 0">
                    <td colspan="8" class="text-center text-muted py-3">No site attendance recorded.</td>
                  </tr>
                </tbody>
              </table>
            </fieldset>
            <br>
            <fieldset class="border p-3">
              <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6">SITE ATTENDANCE SHEET <b-badge variant="success">INDIRECT</b-badge></legend>
              <table class="table table-bordered mb-0 table-sm">
                <thead class="thead-dark">
                  <tr>
                    <th>Date</th>
                    <th>Position</th>
                    <th>Rate</th>
                    <th>Regular Hours</th>
                    <th>OT Hours</th>
                    <th>Total Rate</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row_log, index) in params.indirect_logs" :key="'indirect-' + index">
                    <td>
                      <strong class="cursor-pointer btn-link font-weight-bold" @click="viewDetails(row_log)">{{ row_log.transaction_date }}</strong>
                    </td>
                    <td>{{ row_log.description }}</td>
                    <td><number-format-component :input_value="Number(row_log.daily_rate)" /></td>
                    <td>{{ row_log.regular_hours }}</td>
                    <td>{{ row_log.ot_hours }}</td>
                    <td><number-format-component :input_value="Number(row_log.total)" /></td>
                    <td>
                      <b-badge v-if="row_log.payroll_id != null" variant="success">PAID</b-badge>
                      <b-badge v-else variant="warning">UNPROCESSED</b-badge>
                    </td>
                  </tr>
                  <tr v-if="!params.indirect_logs || params.indirect_logs.length === 0">
                    <td colspan="7" class="text-center text-muted py-3">No indirect site attendance recorded.</td>
                  </tr>
                </tbody>
              </table>
            </fieldset>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab>
        <template v-slot:title>
          <font-awesome-icon icon="clock" /> Office Attendance
          <b-badge variant="light" class="ml-1">{{ officeCount }}</b-badge>
        </template>

        <b-row class="m-0">
          <b-col class="p-0">
            <fieldset class="border p-3">
              <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6">OFFICE ATTENDANCE SHEET <b-badge variant="light" class="text-dark">KIOSK</b-badge></legend>
              <table class="table table-bordered mb-0 table-sm">
                <thead class="thead-dark">
                  <tr>
                    <th>Date</th>
                    <th>Position</th>
                    <th>Rate</th>
                    <th class="text-center">Late (min)</th>
                    <th class="text-center">Reg Hrs</th>
                    <th class="text-center">OT Hrs</th>
                    <th class="text-center">ND Hrs</th>
                    <th class="text-right">Total</th>
                    <th class="text-center">Batch</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row_log in params.office_logs" :key="'office-' + row_log.id">
                    <td>
                      <strong class="cursor-pointer btn-link font-weight-bold" @click="viewOfficeDetails(row_log)">{{ row_log.formatted_date.other_formats.format_1 }}</strong>
                      <b-badge v-if="row_log.holiday_type == 1" variant="primary" class="ml-1">Regular Holiday</b-badge>
                      <b-badge v-else-if="row_log.holiday_type == 2" variant="info" class="ml-1">Special Holiday</b-badge>
                    </td>
                    <td>{{ row_log.position }}</td>
                    <td><number-format-component :input_value="Number(row_log.daily_rate)" /></td>
                    <td class="text-center" :class="{ 'text-danger font-weight-bold': row_log.late_minutes > 0 }">{{ hours(row_log.late_minutes) }}</td>
                    <td class="text-center">{{ hours(row_log.regular_hours) }}</td>
                    <td class="text-center">{{ hours(row_log.ot_hours) }}</td>
                    <td class="text-center">{{ hours(row_log.nd_hours) }}</td>
                    <td class="text-right">
                      <number-format-component v-if="row_log.total !== null" :input_value="Number(row_log.total)" />
                      <span v-else class="text-muted">--</span>
                    </td>
                    <td class="text-center">
                      <span v-if="row_log.json_export_id">#{{ row_log.json_export_id }}</span>
                      <span v-else class="text-muted">--</span>
                    </td>
                    <td><b-badge :variant="row_log.status_variant">{{ row_log.status }}</b-badge></td>
                  </tr>
                  <tr v-if="!params.office_logs || params.office_logs.length === 0">
                    <td colspan="10" class="text-center text-muted py-3">No office attendance recorded on the kiosk.</td>
                  </tr>
                </tbody>
              </table>
              <small class="text-muted d-block mt-2">
                <b-badge variant="secondary">INCOMPLETE</b-badge> day still has an open punch and is not yet counted &middot;
                <b-badge variant="warning">UNPROCESSED</b-badge> waiting to be processed in Attendance &middot;
                <b-badge variant="info">FOR APPROVAL</b-badge> in a batch awaiting approval &middot;
                <b-badge variant="primary">APPROVED</b-badge> approved, not yet paid &middot;
                <b-badge variant="success">PAID</b-badge> included in an office payroll
              </small>
            </fieldset>
          </b-col>
        </b-row>
      </b-tab>
    </b-tabs>

    <!-- Site attendance details -->
    <b-modal v-model="modal" centered size="xl" modal-class="xxl-modal" body-class="p-0" :hide-footer="true" :hide-header="true">
      <div class="card bg-light mb-0">
        <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
          <h6 class="mb-0"><font-awesome-icon icon="list" /> Attendance Details</h6>
        </div>
        <div class="card-body bg-white position-relative">
          <fieldset class="border p-3 mb-2">
            <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6 mb-0 text-uppercase">{{ row_log.transaction_date }}</legend>
            <b-row>
              <b-col>
                <b-form-group label="Position:">
                  <b-form-input v-model="row_log.description" type="text" disabled></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Daily Rate:">
                  <b-form-input v-model="row_log.daily_rate" type="number" step="any" disabled></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Total Regular Hours:">
                  <b-form-input v-model="row_log.regular_hours" type="number" step="any" disabled></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Total OT Hours:">
                  <b-form-input v-model="row_log.ot_hours" type="number" step="any" disabled></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group label="Options" description="This will override the computation">
                  <b-form-checkbox v-model="row_log.is_modified" name="check-button" switch disabled>
                    Override Computation
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <table class="table border">
              <thead class="bg-light text-dark">
                <tr>
                  <th scope="col" class="text-center">AM IN</th>
                  <th scope="col" class="text-center">AM OUT</th>
                  <th scope="col" class="text-center">PM IN</th>
                  <th scope="col" class="text-center">PM OUT</th>
                  <th scope="col" class="text-center">OT IN</th>
                  <th scope="col" class="text-center">OT OUT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b-form-input v-model="row_log.am_in" type="time" disabled></b-form-input></td>
                  <td><b-form-input v-model="row_log.am_out" type="time" disabled></b-form-input></td>
                  <td><b-form-input v-model="row_log.pm_in" type="time" disabled></b-form-input></td>
                  <td><b-form-input v-model="row_log.pm_out" type="time" disabled></b-form-input></td>
                  <td><b-form-input v-model="row_log.ot_in" type="time" disabled></b-form-input></td>
                  <td><b-form-input v-model="row_log.ot_out" type="time" disabled></b-form-input></td>
                </tr>
              </tbody>
            </table>
          </fieldset>
        </div>
      </div>
    </b-modal>

    <!-- Office attendance details -->
    <b-modal v-model="office_modal" centered size="lg" body-class="p-0" :hide-footer="true" :hide-header="true">
      <div class="card bg-light mb-0" v-if="office_row">
        <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
          <h6 class="mb-0"><font-awesome-icon icon="clock" /> Office Attendance Details</h6>
          <b-badge :variant="office_row.status_variant">{{ office_row.status }}</b-badge>
        </div>
        <div class="card-body bg-white">
          <fieldset class="border p-3 mb-0">
            <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6 mb-0 text-uppercase">{{ office_row.formatted_date.other_formats.format_1 }}</legend>
            <b-row class="mb-3">
              <b-col md="3"><small class="text-muted d-block">Position</small><strong>{{ office_row.position || '--' }}</strong></b-col>
              <b-col md="3"><small class="text-muted d-block">Daily Rate</small><strong><number-format-component :input_value="Number(office_row.daily_rate)" /></strong></b-col>
              <b-col md="2"><small class="text-muted d-block">Late (min)</small><strong :class="{ 'text-danger': office_row.late_minutes > 0 }">{{ hours(office_row.late_minutes) }}</strong></b-col>
              <b-col md="2"><small class="text-muted d-block">Reg / OT / ND</small><strong>{{ hours(office_row.regular_hours) }} / {{ hours(office_row.ot_hours) }} / {{ hours(office_row.nd_hours) }}</strong></b-col>
              <b-col md="2"><small class="text-muted d-block">Total</small><strong><number-format-component v-if="office_row.total !== null" :input_value="Number(office_row.total)" /><span v-else>--</span></strong></b-col>
            </b-row>
            <table class="table table-bordered table-sm mb-2">
              <thead class="bg-light text-dark">
                <tr>
                  <th v-for="p in punches" :key="p.key" class="text-center">{{ p.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="p in punches" :key="p.key" class="text-center">
                    <img v-if="office_row[p.key + '_image']" :src="assets_path.OFFICE_LOGS_URL + office_row[p.key + '_image']" class="timelog-thumb d-block mx-auto mb-1" />
                    <span>{{ punchText(office_row['formatted_' + p.key]) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <small class="text-muted">
              <span v-if="office_row.kiosk_device">Recorded on <strong>{{ office_row.kiosk_device.name }}</strong>. </span>
              <span v-if="office_row.json_export_id">Batch #{{ office_row.json_export_id }}. </span>
              <span v-if="office_row.office_payroll_id">Office Payroll #{{ office_row.office_payroll_id }}.</span>
            </small>
          </fieldset>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js';
  import NumberFormatComponent from '@/views/helpers/NumberFormatComponent.vue';
  import { assets_path } from '@/config';

  export default {
    mixins: [SwalMixin],
    components: { NumberFormatComponent },
    props: {
      params: {
        type: Object,
        default: () => ({}),
        required: true
      },
    },
    data() {
      return {
        assets_path: assets_path,
        modal: false,
        row_log: {},
        office_modal: false,
        office_row: null,
        punches: [
          { key: 'am_in', label: 'AM IN' },
          { key: 'am_out', label: 'AM OUT' },
          { key: 'pm_in', label: 'PM IN' },
          { key: 'pm_out', label: 'PM OUT' },
          { key: 'ot_in', label: 'OT IN' },
          { key: 'ot_out', label: 'OT OUT' },
        ],
      }
    },
    computed: {
      siteCount() {
        return (this.params.logs ? this.params.logs.length : 0) + (this.params.indirect_logs ? this.params.indirect_logs.length : 0);
      },
      officeCount() {
        return this.params.office_logs ? this.params.office_logs.length : 0;
      }
    },
    methods: {
      hours(value) {
        if (value === null || value === undefined || value === '') return '--';
        return Number(value);
      },
      punchText(formatted) {
        if (!formatted || formatted.default === '') return '--';
        return formatted.other_formats.format_1;
      },
      save() {
        this.$emit('save');
        this.modal = false;
      },
      viewDetails(row_log) {
        this.modal = true;
        this.row_log = row_log;
      },
      viewOfficeDetails(row_log) {
        this.office_row = row_log;
        this.office_modal = true;
      }
    }
  }
</script>

<style scoped>
  .timelog-thumb {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
</style>
